import React, { useState, useEffect, useCallback } from "react";
import { CreditCard, Check, X, Calendar, RefreshCw, Clock, AlertTriangle, XCircle, ShieldOff, ShieldCheck, Monitor } from "lucide-react";
import { AppButton, AppBadge, ConfirmModal } from "../../../ui";
import { DetailSection } from "../../../ui/DetailRow";
import { Form, Row, Col } from "react-bootstrap";
import apiClient from "../../../../utils/apiClient";

const LIMIT_LABELS = {
  maxStudentsPerBatch: "Students Per Batch",
  maxBatches: "Active Batches",
  maxCourses: "Active Courses",
  maxInstructors: "Instructors",
  maxAdmins: "Admins",
  storageGB: "Storage (GB)",
  maxBranches: "Max Branches",
  maxConcurrentMeetings: "Concurrent Zoom Sessions",
  maxConcurrentZohoMeetings: "Concurrent Zoho Sessions",
};

const TOGGLE_LABELS = {
  zoomV2: "Zoom V2",
  recordings: "Recordings",
  whatsapp: "WhatsApp",
  customDomain: "Custom Domain",
  aiMock: "AI Mock Interview",
  aiLearning: "AI Learning",
  aiQuestionBank: "AI Question Bank",
  feeManagement: "Fee Management",
  enquiryManagement: "Enquiry Management",
  interviews: "Interviews",
  codingPractice: "Coding Practice",
  assignments: "Assignments",
  customBranding: "Custom Branding",
  apiAccess: "API Access",
  enquiryFormBuilder: "Enquiry Form Builder",
  quizAssessment: "Quiz & Assessments",
  selfPacedCourses: "Self-Paced Learning",
  platformHostedZoom: "Platform Hosted Zoom",
  platformHostedZoho: "Platform Hosted Zoho",
  multiBranch: "Multi-Branch",
};

/**
 * PlanTab — Shows the current plan and allows assigning/changing plans.
 */
const PlanTab = ({ institute, onUpdate }) => {
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(institute.planId?._id || "");
  const [billingCycle, setBillingCycle] = useState(institute.billingCycle || "yearly");
  const [saving, setSaving] = useState(false);
  const [activePlan, setActivePlan] = useState(null);
  const [overrideData, setOverrideData] = useState({ disabledFeatures: [], featureOverrides: [] });
  const [overrideSaving, setOverrideSaving] = useState(false);
  const [trialDate, setTrialDate] = useState("");
  const [trialDateSaving, setTrialDateSaving] = useState(false);

  // ── Plan limit overrides (concurrent zoom + zoho sessions) ──
  const [planOverrides, setPlanOverrides] = useState({ maxConcurrentMeetings: "", maxConcurrentZohoMeetings: "" });
  const [planDefaults, setPlanDefaults] = useState({ maxConcurrentMeetings: 0, maxConcurrentZohoMeetings: 0, platformHostedZoho: false, planName: null });
  const [planOverrideSaving, setPlanOverrideSaving] = useState(false);

  const toggleOverride = (key) => {
    setOverrideData(prev => {
      const has = prev.featureOverrides.includes(key);
      const next = has ? prev.featureOverrides.filter(f => f !== key) : [...prev.featureOverrides, key];
      return { ...prev, featureOverrides: next };
    });
  };

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await apiClient.get("/sa/plans");
        setPlans(res.data.plans || []);
      } catch { /* silent */ }
    };
    fetchPlans();
  }, []);

  const fetchOverrides = useCallback(async () => {
    try {
      const res = await apiClient.get(`/sa/institutes/${institute._id}/feature-overrides`);
      setOverrideData(res.data);
    } catch { /* silent */ }
  }, [institute._id]);

  useEffect(() => { fetchOverrides(); }, [fetchOverrides]);

  const fetchPlanOverrides = useCallback(async () => {
    try {
      const res = await apiClient.get(`/sa/institutes/${institute._id}/plan-overrides`);
      const ov = res.data.planOverrides || {};
      setPlanOverrides({
        maxConcurrentMeetings: ov.maxConcurrentMeetings ?? "",
        maxConcurrentZohoMeetings: ov.maxConcurrentZohoMeetings ?? "",
      });
      setPlanDefaults(res.data.planDefaults || { maxConcurrentMeetings: 0, maxConcurrentZohoMeetings: 0, platformHostedZoho: false, planName: null });
    } catch { /* silent */ }
  }, [institute._id]);

  useEffect(() => { fetchPlanOverrides(); }, [fetchPlanOverrides]);

  const handleSavePlanOverrides = async () => {
    try {
      setPlanOverrideSaving(true);
      const zoom = planOverrides.maxConcurrentMeetings === "" ? null : Number(planOverrides.maxConcurrentMeetings);
      const zoho = planOverrides.maxConcurrentZohoMeetings === "" ? null : Number(planOverrides.maxConcurrentZohoMeetings);
      await apiClient.put(`/sa/institutes/${institute._id}/plan-overrides`, {
        maxConcurrentMeetings: zoom,
        maxConcurrentZohoMeetings: zoho,
      });
      setToast({ show: true, type: "success", msg: "Plan overrides saved." });
    } catch (err) {
      setToast({ show: true, type: "error", msg: err.response?.data?.message || "Failed to save overrides." });
    } finally {
      setPlanOverrideSaving(false);
    }
  };

  // Resolve active plan from the plans list
  useEffect(() => {
    if (selectedPlanId && plans.length > 0) {
      const found = plans.find((p) => p._id === selectedPlanId);
      setActivePlan(found || null);
    } else {
      setActivePlan(null);
    }
  }, [selectedPlanId, plans]);

  const [toast, setToast] = useState({ show: false, type: "", msg: "" });

  const handleAssign = async () => {
    try {
      setSaving(true);
      const res = await apiClient.patch(`/sa/institutes/${institute._id}/plan`, {
        planId: selectedPlanId || null,
        billingCycle: selectedPlanId ? billingCycle : "",
      });
      if (onUpdate) onUpdate(res.data.institute);
      setToast({ show: true, type: "success", msg: res.data.message || "Plan updated." });
    } catch (err) {
      console.error("Plan assign error:", err);
      const msg = err.response?.data?.message || err.message || "Failed to update plan.";
      setToast({ show: true, type: "error", msg });
    } finally {
      setSaving(false);
    }
  };

  const [showCancelTrialConfirm, setShowCancelTrialConfirm] = useState(false);

  const handleCancelTrial = () => setShowCancelTrialConfirm(true);

  const confirmCancelTrial = async () => {
    setShowCancelTrialConfirm(false);
    try {
      setSaving(true);
      const res = await apiClient.patch(`/sa/institutes/${institute._id}/status`, {
        status: "suspended",
      });
      if (onUpdate) onUpdate(res.data.institute);
    } catch {
      /* handle error */
    } finally {
      setSaving(false);
    }
  };

  const currentPlan = institute.planId;
  const isTrial = institute.status === "trial";
  const trialExpiry = institute.trialExpiresAt ? new Date(institute.trialExpiresAt) : null;
  const trialDaysLeft = trialExpiry ? Math.max(0, Math.ceil((trialExpiry - new Date()) / (1000 * 60 * 60 * 24))) : null;
  const trialExpired = trialDaysLeft !== null && trialDaysLeft <= 0;

  // Initialize trialDate from institute data
  useEffect(() => {
    if (institute.trialExpiresAt) {
      const d = new Date(institute.trialExpiresAt);
      setTrialDate(d.toISOString().split("T")[0]);
    }
  }, [institute.trialExpiresAt]);

  const handleUpdateTrialDate = async () => {
    if (!trialDate) return;
    try {
      setTrialDateSaving(true);
      const res = await apiClient.patch(`/sa/institutes/${institute._id}/trial-date`, {
        trialExpiresAt: trialDate,
      });
      if (onUpdate) onUpdate(res.data.institute);
      setToast({ show: true, type: "success", msg: res.data.message || "Trial date updated." });
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Failed to update trial date.";
      setToast({ show: true, type: "error", msg });
    } finally {
      setTrialDateSaving(false);
    }
  };

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kolkata" }) : "—";

  return (
    <div className="sa-detail-tab">
      {/* Trial Status Banner */}
      {isTrial && (
        <div
          className="d-flex align-items-center gap-3 p-3 rounded-3 mb-3"
          style={{
            background: trialExpired
              ? "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)"
              : "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
            border: `1px solid ${trialExpired ? "#fecaca" : "#fde68a"}`,
          }}
        >
          {trialExpired ? (
            <AlertTriangle size={20} style={{ color: "#dc2626" }} />
          ) : (
            <Clock size={20} style={{ color: "#d97706" }} />
          )}
          <div style={{ flex: 1 }}>
            <div className="fw-semibold" style={{ fontSize: "0.875rem", color: trialExpired ? "#991b1b" : "#92400e" }}>
              {trialExpired ? "Trial Expired" : "Trial Period Active"}
            </div>
            <div style={{ fontSize: "0.8125rem", color: trialExpired ? "#b91c1c" : "#a16207" }}>
              {trialExpired
                ? `Trial expired on ${formatDate(institute.trialExpiresAt)}`
                : `${trialDaysLeft} day${trialDaysLeft !== 1 ? "s" : ""} remaining — expires ${formatDate(institute.trialExpiresAt)}`}
            </div>
          </div>
          <AppBadge variant="outline" color={trialExpired ? "red" : "amber"}>
            {trialExpired ? "Expired" : `${trialDaysLeft}d left`}
          </AppBadge>
          <AppButton
            variant="outline"
            size="sm"
            onClick={handleCancelTrial}
            disabled={saving}
            style={{ borderColor: "#dc2626", color: "#dc2626", fontSize: "0.75rem" }}
          >
            <XCircle size={14} /> Cancel Trial
          </AppButton>
        </div>
      )}

      {/* Warning: Trial with no plan = all gated features blocked */}
      {isTrial && !currentPlan && (
        <div
          className="d-flex align-items-center gap-3 p-3 rounded-3 mb-3"
          style={{
            background: "linear-gradient(135deg, #fef2f2 0%, #fff1f2 100%)",
            border: "1px solid #fecaca",
          }}
        >
          <AlertTriangle size={20} style={{ color: "#dc2626" }} />
          <div style={{ flex: 1 }}>
            <div className="fw-semibold" style={{ fontSize: "0.875rem", color: "#991b1b" }}>
              No Plan Assigned — All Gated Features Blocked
            </div>
            <div style={{ fontSize: "0.8125rem", color: "#b91c1c" }}>
              This trial institute has no plan assigned. All plan-gated modules (AI, Interviews, Assignments, Fees, etc.) are blocked.
              Assign a trial plan below to grant access.
            </div>
          </div>
        </div>
      )}

      {/* Current Plan Info */}
      <DetailSection title="Trial Period">
        <Row className="g-3 align-items-end">
          <Col md={5}>
            <Form.Group>
              <Form.Label className="small fw-medium">Trial Expiry Date</Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={trialDate}
                onChange={(e) => setTrialDate(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <AppButton
              variant="default"
              size="sm"
              onClick={handleUpdateTrialDate}
              disabled={trialDateSaving || !trialDate}
              className="w-100"
            >
              <Calendar size={14} /> {trialDateSaving ? "Saving…" : "Update Trial Date"}
            </AppButton>
          </Col>
          <Col md={4}>
            {isTrial && trialExpiry && (
              <div className="small text-muted">
                Current: {formatDate(institute.trialExpiresAt)}
                {trialDaysLeft !== null && (
                  <span className={trialExpired ? " text-danger" : " text-warning"}>
                    {" "}({trialExpired ? "Expired" : `${trialDaysLeft}d left`})
                  </span>
                )}
              </div>
            )}
            {!isTrial && (
              <div className="small text-muted">
                Setting a trial date will switch the institute to trial status.
              </div>
            )}
          </Col>
        </Row>
      </DetailSection>

      {/* Current Plan Info */}
      <DetailSection title="Current Plan">
        <div className="d-flex align-items-center gap-3 mb-3">
          <CreditCard size={20} style={{ color: "var(--text-tertiary)" }} />
          <div>
            <div className="fw-semibold" style={{ fontSize: "1rem" }}>
              {currentPlan?.name || "No Plan Assigned"}
            </div>
            {currentPlan && (
              <div className="text-muted" style={{ fontSize: "0.8125rem" }}>
                {institute.billingCycle && `Billed ${institute.billingCycle}`}
                {isTrial && currentPlan && " · Trial plan"}
              </div>
            )}
            {!currentPlan && isTrial && (
              <div className="text-muted" style={{ fontSize: "0.8125rem" }}>
                Institute is on trial with no plan assigned
              </div>
            )}
          </div>
          {currentPlan ? (
            <AppBadge variant="outline" color={isTrial ? "amber" : "emerald"}>
              {isTrial ? "Trial" : "Active"}
            </AppBadge>
          ) : (
            <AppBadge variant="outline" color="amber">Unlimited (No Plan)</AppBadge>
          )}
        </div>

        {/* Subscription / Trial dates */}
        {(institute.subscriptionStart || (isTrial && institute.trialExpiresAt)) && (
          <div className="d-flex gap-4 mb-2" style={{ fontSize: "0.8125rem", color: "var(--text-secondary)" }}>
            {institute.subscriptionStart && (
              <span><Calendar size={14} className="me-1" /> Start: {formatDate(institute.subscriptionStart)}</span>
            )}
            {institute.subscriptionEnd && (
              <span><Calendar size={14} className="me-1" /> End: {formatDate(institute.subscriptionEnd)}</span>
            )}
            {isTrial && institute.trialExpiresAt && !institute.subscriptionEnd && (
              <span><Clock size={14} className="me-1" /> Trial Expires: {formatDate(institute.trialExpiresAt)}</span>
            )}
          </div>
        )}
      </DetailSection>

      {/* Assign / Change Plan */}
      <DetailSection title="Assign Plan">
        <Row className="g-3 align-items-end">
          <Col md={5}>
            <Form.Group>
              <Form.Label className="small fw-medium">Plan</Form.Label>
              <Form.Select
                size="sm"
                value={selectedPlanId}
                onChange={(e) => setSelectedPlanId(e.target.value)}
              >
                <option value="">No Plan (Unlimited)</option>
                {plans.map((p) => (
                  <option key={p._id} value={p._id}>{p.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label className="small fw-medium">Billing Cycle</Form.Label>
              <Form.Select
                size="sm"
                value={billingCycle}
                onChange={(e) => setBillingCycle(e.target.value)}
                disabled={!selectedPlanId}
              >
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={3}>
            <AppButton
              variant="default"
              size="sm"
              onClick={handleAssign}
              disabled={saving}
              className="w-100"
            >
              <RefreshCw size={14} /> {saving ? "Saving…" : "Update Plan"}
            </AppButton>
          </Col>
        </Row>
      </DetailSection>

      {/* Plan Feature Preview */}
      {activePlan && (
        <DetailSection title={`"${activePlan.name}" Plan Features`}>
          <div className="mb-3">
            <div className="small fw-medium text-muted text-uppercase mb-2" style={{ letterSpacing: "0.04em", fontSize: "0.7rem" }}>
              Limits
            </div>
            <div className="d-flex flex-wrap gap-3">
              {Object.entries(LIMIT_LABELS).map(([key, label]) => {
                const planVal = activePlan[key];
                const overrideVal = (key === "maxConcurrentMeetings" || key === "maxConcurrentZohoMeetings")
                  ? planOverrides[key]
                  : "";
                const effective = overrideVal !== "" ? Number(overrideVal) : planVal;
                const isOverridden = overrideVal !== "";
                return (
                <div key={key} className="d-flex align-items-center gap-1" style={{ fontSize: "0.8125rem", minWidth: "140px" }}>
                  <span className="text-muted">{label}:</span>
                  <span className={`fw-medium${isOverridden ? " text-primary" : ""}`}>
                    {effective ? effective : "Unlimited"}
                    {key === "storageGB" && effective ? " GB" : ""}
                    {isOverridden && " ✎"}
                  </span>
                </div>
                );
              })}
            </div>
          </div>

          <div>
            <div className="small fw-medium text-muted text-uppercase mb-2" style={{ letterSpacing: "0.04em", fontSize: "0.7rem" }}>
              Features
            </div>
            <div className="d-flex flex-wrap gap-3">
              {Object.entries(TOGGLE_LABELS).map(([key, label]) => (
                <div key={key} className="d-flex align-items-center gap-1" style={{ fontSize: "0.8125rem", minWidth: "160px" }}>
                  {activePlan[key] ? (
                    <Check size={14} className="text-success" />
                  ) : (
                    <X size={14} className="text-muted" />
                  )}
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </div>
        </DetailSection>
      )}

      {/* Feature Overrides — only shown when platform has disabled features */}
      {overrideData.disabledFeatures.length > 0 && (
        <DetailSection title="Feature Overrides">
          <p className="text-muted" style={{ fontSize: "0.8125rem", marginBottom: "0.75rem" }}>
            These features are globally disabled by the platform. Toggle on to enable for this institute (e.g. for testing).
          </p>
          <div className="d-flex flex-wrap gap-3 mb-3">
            {overrideData.disabledFeatures.map((key) => {
              const isOverridden = overrideData.featureOverrides.includes(key);
              return (
                <div key={key} className="d-flex align-items-center gap-2" style={{ fontSize: "0.8125rem", minWidth: "200px" }}>
                  {isOverridden ? (
                    <ShieldCheck size={14} className="text-success" />
                  ) : (
                    <ShieldOff size={14} className="text-muted" />
                  )}
                  <Form.Check
                    type="switch"
                    id={`override-${key}`}
                    label={TOGGLE_LABELS[key] || key}
                    checked={isOverridden}
                    onChange={() => toggleOverride(key)}
                  />
                </div>
              );
            })}
          </div>
          <AppButton
            variant="default"
            size="sm"
            disabled={overrideSaving}
            onClick={async () => {
              try {
                setOverrideSaving(true);
                await apiClient.put(`/sa/institutes/${institute._id}/feature-overrides`, {
                  featureOverrides: overrideData.featureOverrides,
                });
              } catch { /* silent */ } finally {
                setOverrideSaving(false);
              }
            }}
          >
            {overrideSaving ? "Saving…" : "Save Overrides"}
          </AppButton>
        </DetailSection>
      )}

      {/* Plan Limit Overrides — Zoom Concurrent Sessions */}
      {currentPlan && (
        <DetailSection title="Plan Limit Overrides">
          <p className="text-muted" style={{ fontSize: "0.8125rem", marginBottom: "0.75rem" }}>
            Override plan limits for this institute. Leave blank to use the plan default. Set 0 for unlimited.
          </p>
          <Row className="g-3 align-items-end">
            <Col md={4}>
              <Form.Group>
                <Form.Label style={{ fontSize: "0.8125rem", fontWeight: 500 }}>
                  <Monitor size={14} className="me-1" />
                  Concurrent Zoom Sessions
                </Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  min={0}
                  placeholder={`Plan default: ${planDefaults.maxConcurrentMeetings || "Unlimited"}`}
                  value={planOverrides.maxConcurrentMeetings}
                  onChange={(e) => setPlanOverrides(prev => ({ ...prev, maxConcurrentMeetings: e.target.value }))}
                />
                <Form.Text className="text-muted" style={{ fontSize: "0.7rem" }}>
                  Plan "{planDefaults.planName || currentPlan.name}" allows {planDefaults.maxConcurrentMeetings || "unlimited"} session{planDefaults.maxConcurrentMeetings !== 1 ? "s" : ""}.
                  {planOverrides.maxConcurrentMeetings !== "" && (
                    <> Override: <strong>{Number(planOverrides.maxConcurrentMeetings) === 0 ? "Unlimited" : planOverrides.maxConcurrentMeetings}</strong> session{Number(planOverrides.maxConcurrentMeetings) !== 1 ? "s" : ""}.</>
                  )}
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label style={{ fontSize: "0.8125rem", fontWeight: 500 }}>
                  <Monitor size={14} className="me-1" />
                  Concurrent Zoho Sessions
                </Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  min={0}
                  placeholder={`Plan default: ${planDefaults.maxConcurrentZohoMeetings || "Disabled"}`}
                  value={planOverrides.maxConcurrentZohoMeetings}
                  onChange={(e) => setPlanOverrides(prev => ({ ...prev, maxConcurrentZohoMeetings: e.target.value }))}
                  disabled={!planDefaults.platformHostedZoho}
                />
                <Form.Text className="text-muted" style={{ fontSize: "0.7rem" }}>
                  {planDefaults.platformHostedZoho
                    ? <>Plan "{planDefaults.planName || currentPlan.name}" allows {planDefaults.maxConcurrentZohoMeetings || "0"} Zoho session{planDefaults.maxConcurrentZohoMeetings !== 1 ? "s" : ""}.</>
                    : <>Platform-hosted Zoho is disabled on this plan. Enable it in the plan to allow overrides.</>
                  }
                  {planOverrides.maxConcurrentZohoMeetings !== "" && (
                    <> Override: <strong>{Number(planOverrides.maxConcurrentZohoMeetings) === 0 ? "Disabled" : planOverrides.maxConcurrentZohoMeetings}</strong> session{Number(planOverrides.maxConcurrentZohoMeetings) !== 1 ? "s" : ""}.</>
                  )}
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={2}>
              <AppButton
                variant="default"
                size="sm"
                onClick={handleSavePlanOverrides}
                disabled={planOverrideSaving}
              >
                {planOverrideSaving ? "Saving…" : "Save Overrides"}
              </AppButton>
            </Col>
            {(planOverrides.maxConcurrentMeetings !== "" || planOverrides.maxConcurrentZohoMeetings !== "") && (
              <Col md={2}>
                <AppButton
                  variant="outline"
                  size="sm"
                  disabled={planOverrideSaving}
                  onClick={async () => {
                    setPlanOverrides({ maxConcurrentMeetings: "", maxConcurrentZohoMeetings: "" });
                    try {
                      setPlanOverrideSaving(true);
                      await apiClient.put(`/sa/institutes/${institute._id}/plan-overrides`, {
                        maxConcurrentMeetings: null,
                        maxConcurrentZohoMeetings: null,
                      });
                      setToast({ show: true, type: "success", msg: "Overrides cleared." });
                    } catch (err) {
                      setToast({ show: true, type: "error", msg: "Failed to clear overrides." });
                    } finally {
                      setPlanOverrideSaving(false);
                    }
                  }}
                >
                  Clear All
                </AppButton>
              </Col>
            )}
          </Row>
        </DetailSection>
      )}

      <ConfirmModal
        show={showCancelTrialConfirm}
        onHide={() => setShowCancelTrialConfirm(false)}
        onConfirm={confirmCancelTrial}
        title="Cancel Trial"
        message="Cancel this institute's trial? This will suspend their account."
        confirmText="Cancel Trial"
        variant="danger"
      />
    </div>
  );
};

export default PlanTab;
